import type { FC } from "react";
import type { Doc } from "sharedb/lib/client";
import { useBoolean } from "usehooks-ts";

import type { BlockType, FieldType, WorksheetType } from "../../shared/types/worksheet";
import { STATIC_CONTENT_FIELD_TYPES, isQuestionBlock } from "../../shared/worksheets/constants";

import { TemplateBlockModal } from "./TemplateBlockModal";

import { t } from "i18n/i18n";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { PageIcon } from "mds/icons";
import type { DocViewContextType } from "providers/DocViewProvider";
import { useDocSelectedValue } from "sharedb/ShareDBDoc";
import { blockHasDifferingSeedText } from "worksheets/field-versioning";

// eslint-disable-next-line react-refresh/only-export-components
export function getTemplateFieldIndex({
  accessId,
  block,
  canViewTemplateModal,
  showResponses,
}: {
  accessId: string;
  block: BlockType | undefined;
  canViewTemplateModal: boolean;
  showResponses: DocViewContextType["showResponses"];
}): number {
  if (!block || !accessId || !canViewTemplateModal || showResponses) {
    return -1;
  }

  // Never show this affordance for static-only blocks.
  if (!isQuestionBlock(block)) {
    return -1;
  }

  const firstNonStaticFieldIndex = (block.f as FieldType[]).findIndex(
    (f) => f && !STATIC_CONTENT_FIELD_TYPES.includes(f.t),
  );

  return firstNonStaticFieldIndex !== -1 ? firstNonStaticFieldIndex : 0;
}

type TemplateBlockButtonProps = {
  block: BlockType;
  doc: Doc<WorksheetType>;
  accessId: string;
  useLinkedSeeding: boolean;
  versionType: "instructor" | "template";
};

export const TemplateBlockButton: FC<TemplateBlockButtonProps> = ({
  block,
  doc,
  accessId,
  useLinkedSeeding,
  versionType,
}) => {
  const showButton = useDocSelectedValue(doc, (current) => {
    if (!current?.data) return false;
    return blockHasDifferingSeedText(current, accessId, block.id, { useLinkedSeeding });
  });
  const { value: modalIsShown, setTrue: showModal, setFalse: hideModal } = useBoolean(false);

  if (!showButton) return null;

  return (
    <li>
      <SquareIconButton
        aria-label={t(`worksheet.template_block_modal.${versionType}.title`)}
        kind="tertiary"
        size="s"
        onClick={showModal}
      >
        <PageIcon />
      </SquareIconButton>
      <TemplateBlockModal
        block={block}
        doc={doc}
        open={modalIsShown}
        versionType={versionType}
        onClose={hideModal}
      />
    </li>
  );
};
